/**
 * WPMMeter Component - Real-time Speaking Speed Display
 * Optimized with React.memo to prevent unnecessary re-renders
 */
import React, { memo } from "react";
import { Badge } from "@/components/ui/badge";
import { Activity } from "lucide-react";
import { getWPMStatus, getWPMMeterValue, WPM_CONFIG } from "./constants";

const WPMMeter = memo(({ wpm, isActive }) => {
  const status = getWPMStatus(wpm);
  const meterValue = getWPMMeterValue(wpm);
  
  return (
    <div className={`p-4 rounded-xl border-2 transition-all duration-300 ${
      isActive ? 'border-purple-500 shadow-lg' : 'border-gray-200'
    } ${status.bgColor}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Activity className={`w-5 h-5 ${isActive ? 'animate-pulse' : ''} ${status.textColor}`} />
          <span className="font-medium text-gray-800">Speaking Speed</span>
        </div>
        <Badge className={status.bgColor + " " + status.textColor}>
          {status.status}
        </Badge>
      </div>
      
      {/* WPM Display */}
      <div className="text-center my-3">
        <span className={`text-4xl font-bold ${status.textColor}`}>{wpm}</span>
        <span className="text-gray-500 text-sm ml-1">WPM</span>
      </div>
      
      {/* Progress Bar with zones */}
      <div className="relative h-4 bg-gray-200 rounded-full overflow-hidden mb-2">
        {/* Zone indicators */}
        <div className="absolute inset-0 flex">
          <div className="w-[50%] bg-gradient-to-r from-red-200 via-orange-200 to-yellow-200" />
          <div className="w-[15%] bg-green-200" />
          <div className="w-[10%] bg-green-300" />
          <div className="w-[25%] bg-gradient-to-r from-yellow-200 via-orange-200 to-red-200" />
        </div>
        
        {/* Current position indicator */}
        <div 
          className="absolute top-0 h-full w-1.5 bg-gray-800 transition-all duration-300 rounded-full shadow-lg"
          style={{ left: `${Math.min(meterValue, 98)}%` }}
        />
      </div>
      
      {/* Zone labels */}
      <div className="flex justify-between text-xs text-gray-500 mt-1">
        <span>0</span>
        <span className="text-red-500">Slow</span>
        <span className="text-green-600 font-medium">120-150</span>
        <span className="text-red-500">Fast</span>
        <span>200+</span>
      </div>
      
      {/* Ideal range indicator */}
      <p className="text-center text-xs text-gray-600 mt-2">
        Ideal Range: <span className="font-semibold text-green-600">120-150 WPM</span>
      </p>
    </div>
  );
});

WPMMeter.displayName = "WPMMeter";

export default WPMMeter;
